import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import css from '@src/styles/components/form/control.scss';
import NumberFormatter from '../../NumberFormatter';
import Label from './Label';

const config = {
    debounceTimeout: 1500,
};

export default class Input extends React.Component {

    static NUMBER_FORMAT = 'number';

    constructor(props) {

        super(props);

        this.state = {
            value: null,
            debounceTimeout: props.debounceTimeout ?? config.debounceTimeout,
        };

        this.nodes = {
            input: null,
        };
    }

    static get propTypes() {

        return {
            defaultClassName: PropTypes.string,
            className: PropTypes.string,
            groupClassName: PropTypes.string,
            label: PropTypes.string,
            name: PropTypes.string,
            type: PropTypes.string,
            value: PropTypes.any,
            format: PropTypes.string,
            suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            onChange: PropTypes.func,
            style: PropTypes.object,
            data: PropTypes.object,
            min: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(NumberFormatter)]),
            max: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(NumberFormatter)]),
            debounceTimeout: PropTypes.number,
            placeholder: PropTypes.string,
            onInput: PropTypes.func,
        };
    }

    render() {

        const groupClasses = [style('form-group', css)];
        if (this.props.groupClassName != undefined)
            groupClasses.push(this.props.groupClassName);

        const classes = [this.props.defaultClassName ?? style('form-control w-full title3', css)];
        if (this.props.className != undefined)
            classes.push(this.props.className);

        const extraAttributes = {};
        if (this.props.data != undefined) {

            Object.keys(this.props.data).forEach((key) => {
            
                extraAttributes[`data-${key}`] = this.props.data[key];
            });
        }

        if (this.props.min)
            extraAttributes['min'] = NumberFormatter.parse(this.props.min).toFloat();

        if (this.props.max)
            extraAttributes['max'] = NumberFormatter.parse(this.props.max).toFloat();

        if (this.props.format == Input.NUMBER_FORMAT) {

            extraAttributes['pattern'] = '[0-9]*';
            extraAttributes['inputMode'] = 'numeric';
        }

        return (
            <div className={ groupClasses.join(' ') }>

                { this.renderLabel() }

                <input
                    ref={ (node) => { this.nodes.input = node; } }
                    className={ classes.join(' ') }
                    type={ this.props.type ?? 'text' }
                    name={ this.props.name }
                    placeholder={ this.props.placeholder }
                    style={ this.props.style }
                    onInput={ this.props.onInput }
                    defaultValue={ this.getValueFrom(this.props.value, NumberFormatter.DEFAULT_DECIMAL_DELIMITER) }
                    { ...extraAttributes }
                />

            </div>
        );
    }

    renderLabel() {

        const { label } = this.props;

        return label
            ? <Label>{ label }</Label>
            : null;
    }

    get value() {

        return this.nodes.input.value;
    }

    getValueFrom(value, forceDecimalDelimiter = null) {

        if (this.props.format == Input.NUMBER_FORMAT) {

            const number = NumberFormatter.parseEx(value, {
                currency: this.props.suffix,
                decimalDelimiter: forceDecimalDelimiter,
            });

            if (this.props?.min?.value != undefined && this.props.min.value > number.value)
                number.set(this.props.min);

            if (this.props?.max?.value != undefined && this.props.max.value < number.value)
                number.set(this.props.max);

            return number.formatted();
        }

        return value;
    }

    setValue(value) {

        if (this.props.format == Input.NUMBER_FORMAT)
            this.nodes.input.value = this.getValueFrom(value);
    }

    componentDidMount() {
        
        let getValueFunc = () => this.nodes.input.value;

        if (this.props.format == Input.NUMBER_FORMAT) {

            // change get value function
            getValueFunc = () => this.getValueFrom(this.nodes.input.value);
        }

        // handle input event
        if (this.state.debounceTimeout > 0) {

            let idTimeout = null;
            this.nodes.input.addEventListener('input', () => {

                if (idTimeout)
                    clearTimeout(idTimeout);

                idTimeout = setTimeout(() => {

                    idTimeout = null;
                    if (this.nodes.input)
                        this.nodes.input.dispatchEvent(new Event('change'));

                }, this.state.debounceTimeout);
            });
        }
        else {

            this.nodes.input.addEventListener('input', () => {

                this.nodes.input.dispatchEvent(new Event('change'));
            });
        }

        // handle change event
        let lastValue = this.nodes.input.value;
        this.nodes.input.addEventListener('change', (e) => {

            this.nodes.input.value = getValueFunc();
            if (lastValue == this.nodes.input.value) {

                // value is not changed

                e.preventDefault();
                e.stopPropagation();
            }
            else {

                /* eslint-disable no-lonely-if */
                if (this.props.onChange != undefined)
                    this.props.onChange(e);
            }

            lastValue = this.nodes.input.value;

        }, { capture: true });

        if (this.props.style != undefined) {

            Object
                .keys(this.props.style)
                .forEach((key) => {

                    if (/^--/.test(key))
                        this.nodes.input.style.setProperty(key, this.props.style[key]);
                });
        }
    }
}